import {Color} from '@benchling/nucleus-ui';
import {UnstyledButton, Modal, Flex, Text, Button, Box, Group} from '@mantine/core';
import {useDisclosure} from '@mantine/hooks';
import {css, StyleSheet} from 'aphrodite';
import DOMPurify from 'dompurify';
import reactUpdate from 'immutability-helper';
import React, {memo, useState} from 'react';
import {v4 as uuidv4} from 'uuid';

import {EMPTY_MRV} from 'chemistry/ChemistryConstants';
import {parseReactionComponents} from 'chemistry/ChemistryDAO';
import {
  ChemicalStructureDrawer,
  ChemicalStructureDrawerMode,
  ChemicalStructureDrawerValue,
} from 'chemistry/marvinjs-wrappers/ChemicalStructureDrawer';
import {MarvinChemicalFormat} from 'chemistry/marvinjs-wrappers/MarvinJSConstants';
import {
  StoichiometryStructuredTableMetadata,
  StoichiometryStructuredTableRowMetadata,
} from 'notebook/integrations/chemistry/StoichiometryStructuredTableAdapter';
import {
  StructuredTable,
  StructuredTableColumn,
  StructuredTableCustomComponentSlotProps,
  StructuredTableRow,
} from 'notebook/structured-tables/StructuredTableConstants';
import getBlankRow from 'notebook/structured-tables/util/getBlankRow';
import {BaseTypedValueType} from 'notebook/tables/TableConstants';
import useResizeSVG from 'ui-library/hooks/useResizeSVG';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import ModalFooter from 'ui-library/modals/ModalFooter';
import {withLoadingScreen} from 'util/Promises';

interface ReactionSketcherModalProps {
  onClose: () => void;
  onSubmit: (mrvStructure: string) => void;
  initialMrvStructure: ChemicalStructureDrawerValue;
}

function ReactionSketcherModal({
  onClose,
  onSubmit,
  initialMrvStructure,
}: ReactionSketcherModalProps): JSX.Element {
  const [structureContent, setStructureContent] = useState<string | null>(null);
  const [hasReactionContents, setHasReactionContents] = useState(false);

  return (
    <Modal
      title="Reaction Sketcher"
      opened={true}
      onClose={onClose}
      size="xl"
      styles={{
        content: {
          height: '90vh',
          display: 'flex',
          flexDirection: 'column',
        },
        body: {
          flex: 1,
        },
      }}
    >
      <Flex direction="column" h="100%">
        <ChemicalStructureDrawer
          mode={ChemicalStructureDrawerMode.REACTION}
          onMoleculeChange={setStructureContent}
          setHasReactionContents={setHasReactionContents}
          initialValue={initialMrvStructure}
          inputFormat={MarvinChemicalFormat.MRV}
          outputFormat={MarvinChemicalFormat.MRV}
        />
        <ModalFooter>
          <Group position="right">
            <Button type="button" onClick={onClose} variant="secondary">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!hasReactionContents}
              onClick={() => {
                onSubmit(structureContent!);
              }}
            >
              Save
            </Button>
          </Group>
        </ModalFooter>
      </Flex>
    </Modal>
  );
}

async function handleReactionSketcherSubmit(
  mrvStructure: string,
  structuredTable: StructuredTable<
    StoichiometryStructuredTableMetadata,
    StoichiometryStructuredTableRowMetadata
  >,
  updateHandler: (structuredTable: StoichiometryStructuredTableMetadata) => void,
  setReactionBase64SVG: (base64SVG: string) => void
): Promise<void> {
  const {reactants, products, base64SVG} = await parseReactionComponents(mrvStructure);
  setReactionBase64SVG(base64SVG);

  const nameColumnIndex = structuredTable.columns.findIndex((col) => col.name === 'Compound');
  const roleColumnIndex = structuredTable.columns.findIndex((col) => col.name === 'Role');

  if (nameColumnIndex === -1 || roleColumnIndex === -1) {
    throw new Error('Could not find required columns');
  }

  const reactantRows = createRowsForReactionComponent(
    reactants,
    'Reactant',
    nameColumnIndex,
    roleColumnIndex,
    structuredTable.columns,
    0
  );

  const productRows = createRowsForReactionComponent(
    products,
    'Product',
    nameColumnIndex,
    roleColumnIndex,
    structuredTable.columns,
    reactantRows.length
  );

  const newRows = [...reactantRows, ...productRows];

  // Overwrite the table rows.
  updateHandler({
    ...structuredTable,
    rows: newRows,
  });
}

function createRowsForReactionComponent(
  components: Array<{moleculeId: string; name: string}>,
  role: 'Reactant' | 'Product',
  nameColumnIndex: number,
  roleColumnIndex: number,
  columns: Array<StructuredTableColumn>,
  startRowIndex: number
): Array<StructuredTableRow<StoichiometryStructuredTableRowMetadata>> {
  const rows: Array<StructuredTableRow<StoichiometryStructuredTableRowMetadata>> = [];
  let rowIndex = startRowIndex;

  for (const component of components) {
    const blankCells = getBlankRow(rowIndex, columns, null);
    const cells = reactUpdate(blankCells, {
      [nameColumnIndex]: {
        text: {$set: component.name},
        typedValue: {
          $set: {
            type: BaseTypedValueType.BIOENTITY,
            bioentitySummary: {id: component.moleculeId, name: component.name},
          },
        },
      },
      [roleColumnIndex]: {
        text: {$set: role},
        typedValue: {$set: {type: BaseTypedValueType.STRING, rawValue: role}},
      },
    });

    rows.push({
      cells,
      rowId: uuidv4(),
      isFinalized: false,
      metadata: {},
      lastSyncedUpstreamValues: null,
    });
    rowIndex++;
  }

  return rows;
}

export type StoichiometryTableEmbeddedChemicalSketcherProps = StructuredTableCustomComponentSlotProps<
  StoichiometryStructuredTableMetadata,
  StoichiometryStructuredTableRowMetadata
>;

/** A chemical sketcher rendered inside a Stoichiometry Structured Table. It can be used to populate the
 * table with reactants and products.
 */
function StoichiometryTableEmbeddedChemicalSketcher(
  props: StoichiometryTableEmbeddedChemicalSketcherProps
): JSX.Element {
  const [isSketcherModalOpen, {open: openSketcherModal, close: closeSketcherModal}] = useDisclosure(false);
  const [reactionBase64SVG, setReactionBase64SVG] = useState<string | null>(null);
  const [previousStructure, setPreviousStructure] = useState<ChemicalStructureDrawerValue>(EMPTY_MRV);
  const reactionSVGRef = useResizeSVG<HTMLDivElement>(css(styles.reactionSVG));

  return (
    <>
      {isSketcherModalOpen && (
        <ReactionSketcherModal
          initialMrvStructure={previousStructure}
          onClose={closeSketcherModal}
          onSubmit={async (mrvStructure: string): Promise<void> => {
            closeSketcherModal();
            setPreviousStructure(mrvStructure);
            await withLoadingScreen(
              handleReactionSketcherSubmit(
                mrvStructure,
                props.structuredTable,
                props.updateHandler,
                setReactionBase64SVG
              )
            );
          }}
        />
      )}
      <UnstyledButton
        role="button"
        tabIndex={0}
        aria-label="Open reaction sketcher"
        onClick={openSketcherModal}
        h="150px"
        w="100%"
        style={{
          border: `1px solid ${Color.GRAY_250}`,
          borderTop: 'none',
        }}
      >
        <Flex direction="column" justify="center" align="center" h="100%">
          {reactionBase64SVG ? (
            <Box
              ref={reactionSVGRef}
              role="img"
              aria-label="Reaction diagram"
              w="100%"
              h="100%"
              p="12px"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              // oxlint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(atob(reactionBase64SVG))}}
            />
          ) : (
            <>
              <BenchlingIcon
                icon="molecule"
                style={{width: '24px', height: '24px', color: Color.GRAY_600}}
                altText="Molecule"
              />
              <Text size="xl" color="dimmed">
                Click to start sketching
              </Text>
            </>
          )}
        </Flex>
      </UnstyledButton>
    </>
  );
}

export default memo(StoichiometryTableEmbeddedChemicalSketcher);

const styles = StyleSheet.create({
  // Ensure SVG scales proportionally within the container. useResizeSVG expects a single className arg so
  // define this style but prefer Mantine props otherwise.
  reactionSVG: {
    maxWidth: '100%',
    maxHeight: '100%',
  },
});
